/**
 * One definition of "is this value empty" and "how does a value read" for the
 * whole CRM.
 *
 * Extracted from utilities/recordSchema so the schema-driven record view and the
 * shared <InfoField> cannot disagree about it. They previously did: the record
 * view rendered an em-dash, InfoField rendered "Not Provided", and the two sat
 * inches apart on the registration tab.
 */

export const EMPTY_DISPLAY = "—";

/**
 * Is this value absent for display purposes?
 *
 * `false` and `0` are REAL answers. InfoField used to be `value ? value :
 * "Not Provided"`, so a merchant who answered "no" to a risk question, or 0%
 * mail order, rendered as having told us nothing — across MID Detail, Company
 * Overview, DBA rows and MID applications. That is the bug this function exists
 * to prevent, so do NOT "simplify" it back to a truthiness check.
 */
export function isEmptyValue(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  return false;
}

/**
 * Render a stored value as display text. Deliberately conservative — it handles
 * shapes, not units, and never guesses that a number is dollars.
 */
export function displayValue(value) {
  if (isEmptyValue(value)) return EMPTY_DISPLAY;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") {
    // Never let a display helper crash a page. JSON.stringify throws on any
    // circular structure — a React element reaches this branch as a fiber tree
    // and takes the whole render down ("Converting circular structure to
    // JSON"). Callers should not pass elements here (InfoField renders them
    // directly), but a helper this widely used must fail soft.
    try {
      return JSON.stringify(value);
    } catch {
      return EMPTY_DISPLAY;
    }
  }
  return String(value);
}

/**
 * Same contract as displayValue, but for a block that can afford height — an
 * object pretty-prints across lines instead of collapsing to one.
 *
 * Exists for the audit log's before/after panes. `change.before` and
 * `change.after` are untyped: the writers put whatever the change happened to
 * be in them, so 873 of 2,864 prod records hold an OBJECT — a field map from
 * MID Update, a whole link document from NMI Link. The pane rendered
 * `{value || "-"}` raw, React threw "Objects are not valid as a React child",
 * and because sound-app's only ErrorBoundary is app-level, one MID Update took
 * the entire page to the error screen rather than breaking a single card.
 *
 * The caller must render this in something that honours newlines
 * (`whitespace-pre-wrap`), or the indentation collapses and there was no point.
 */
export function displayValueMultiline(value) {
  if (isEmptyValue(value)) return EMPTY_DISPLAY;
  if (value !== null && typeof value === "object" && !Array.isArray(value)) {
    // Same circular-structure hazard displayValue guards above.
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return EMPTY_DISPLAY;
    }
  }
  return displayValue(value);
}
